import dayjs from "dayjs";
import minMax from "dayjs/plugin/minMax";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import RegistryManager from "../services/registry/Manager";
import {DATE_SHORT} from "./constants";

dayjs.extend(minMax);
dayjs.extend(utc);
dayjs.extend(tz);
dayjs.extend(relativeTime);

export const formatDate = (timestamp, userSettings) => {
  const {timezone = "iso_utc", dateFormat = "YYYY-MM-DDTHH:mm:ss.000Z"} =
    userSettings || RegistryManager.userSettings || {};

  // There isn't a setting for this now, but might
  // be a future enhancement in SecureX
  if (timezone === "locale") {
    return dateLocaleString(timestamp);
  }

  const date = dayjs(timestamp);

  if (timezone !== "iso_utc") {
    date.tz(timezone);
  }

  return date.format(dateFormat);
};

export const toNow = timestamp => {
  return dayjs(timestamp).toNow(true);
};

export const minimumDate = timeList => {
  return dayjs.min(timeList);
};

export const maximumDate = timeList => {
  return dayjs.max(timeList);
};

export const dateLocaleString = timestamp => {
  const date = timestamp ? new Date(timestamp) : new Date();

  return date.toLocaleString(navigator.language, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric"
  });
};

export const dayjsInstance = dayjs;

export const SecondsPerMin = 60;
export const SecondsPerHour = SecondsPerMin * 60;
export const SecondsPerDay = SecondsPerHour * 24;
export const SecondsPerYear = SecondsPerDay * 365;
export const getTimeSince = timestamp => {
  const diff = (Date.now() - new Date(timestamp)) / 1000,
    secondsPast = diff < 0 ? diff * -1 : diff,
    future = diff < 0;

  if (secondsPast < SecondsPerMin) {
    return {seconds: secondsPast};
  }

  if (secondsPast < SecondsPerHour) {
    return {
      minutes: parseInt((secondsPast % SecondsPerHour) / 60, 10),
      seconds: parseInt(secondsPast / 60, 10)
    };
  }

  if (secondsPast <= SecondsPerDay) {
    const hourValue = parseInt(secondsPast / SecondsPerHour, 10);
    const remainingMinutes = parseInt((secondsPast % SecondsPerHour) / 60, 10);

    return {
      hours: hourValue,
      minutes: remainingMinutes
    };
  }

  const dayValue = parseInt(secondsPast / SecondsPerDay, 10);
  const remainingHours = parseInt(
    (secondsPast % SecondsPerDay) / SecondsPerHour,
    10
  );
  const remainingMinutes = parseInt((secondsPast % SecondsPerHour) / 60, 10);

  if (!remainingHours && !remainingMinutes) {
    return {days: dayValue};
  }

  if (!remainingHours) {
    return {days: dayValue, minutes: remainingMinutes};
  }

  if (!remainingMinutes) {
    return {days: dayValue, hours: remainingHours};
  }

  return {
    days: dayValue,
    hours: remainingHours,
    minutes: remainingMinutes,
    future
  };
};

export const getTimeSinceString = (timestamp, options = {}) => {
  const {
      language = navigator.language,
      nowForSeconds = false,
      style = "short",
      returnDateBeforeYesterday = false
    } = options,
    timeSince =
      !!timestamp && timestamp.constructor === Object
        ? timestamp
        : getTimeSince(timestamp, options),
    {future} = timeSince,
    modifier = future ? 1 : -1,
    rtf = new Intl.RelativeTimeFormat(language, {style});

  if (timeSince.hasOwnProperty("days")) {
    if (returnDateBeforeYesterday && timeSince.days > 2) {
      return new Date(timestamp).toLocaleDateString();
    }
    return rtf.format(timeSince.days * modifier, "days");
  } else if (timeSince.hasOwnProperty("hours")) {
    return rtf.format(timeSince.hours * modifier, "hours");
  } else if (timeSince.hasOwnProperty("minutes")) {
    return rtf.format(timeSince.minutes * modifier, "minutes");
  }

  if (nowForSeconds) {
    return "now";
  }

  return rtf.format(timeSince.seconds * modifier, "seconds");
};

/**
 * returns dayjs obj
 * @param {Number} count number of days, weeks, months, etc.
 * @param {String} interval unit of time days, weeks, months, etc.
 */
export const calculateEndDateFromNow = (count, interval) => {
  const unitOfTime = DATE_SHORT[interval];
  return dayjs().add(count, unitOfTime);
};
